import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Alert, Spinner, Badge } from 'react-bootstrap';
import authService from '../services/authService';
import businessService from '../services/businessService';

function QuickStartDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [businessName, setBusinessName] = useState('');
  const [exitStrategy, setExitStrategy] = useState('');
  const [targetAmount, setTargetAmount] = useState('');
  const [isExploringGuided, setIsExploringGuided] = useState(false);

  useEffect(() => {
    checkAuthAndLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuthAndLoad = async () => {
    const result = await authService.checkAuth();
    if (!result.authenticated) {
      navigate('/login');
      return;
    }

    // Check if this is actually a quick-start user
    const statusResponse = await fetch('/api/quick-start/get-status', {
      method: 'GET',
      credentials: 'include'
    });

    if (statusResponse.ok) {
      const status = await statusResponse.json();

      // If not quick-start or quick-start-exploring, redirect to main dashboard
      if (status.onboarding_path !== 'quick_start' && status.onboarding_path !== 'quick_start_exploring') {
        navigate('/dashboard');
        return;
      }

      // Track if they're in exploring mode
      if (status.onboarding_path === 'quick_start_exploring') {
        setIsExploringGuided(true);
      }

      // If quick-start not completed, redirect to continue it
      if (!status.quick_start_completed) {
        navigate('/quick-start');
        return;
      }
    }

    // Load business info for display
    const nameResult = await businessService.getField('business_name');
    if (nameResult.success && nameResult.data.field_value) {
      setBusinessName(nameResult.data.field_value);
    }

    const exitResult = await businessService.getField('exit_strategy');
    if (exitResult.success && exitResult.data.field_value) {
      const exitMap = {
        'lifestyle': 'Lifestyle Business',
        'build_to_sell': 'Build to Sell',
        'acquisition': 'Strategic Acquisition',
        'legacy': 'Legacy/Family Business'
      };
      setExitStrategy(exitMap[exitResult.data.field_value] || exitResult.data.field_value);
    }

    const targetResult = await businessService.getField('exit_target_amount');
    if (targetResult.success && targetResult.data.field_value) {
      setTargetAmount(targetResult.data.field_value);
    }

    setLoading(false);
  };

  const handleExploreGuidedJourney = async () => {
    try {
      // Call API to switch to 'quick_start_exploring' mode
      // This updates the onboarding_path in the database
      const response = await fetch('/api/quick-start/switch-to-guided', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // Update local state immediately so UI reflects the change
        setIsExploringGuided(true);
        // Navigate to main dashboard - they can explore and edit their data
        navigate('/dashboard');
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to switch to guided journey mode:', errorData.error);
        // Try navigating anyway
        navigate('/dashboard');
      }
    } catch (err) {
      console.error('Error switching to guided journey:', err);
      // Still navigate even if API call fails
      navigate('/dashboard');
    }
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col md={10} lg={8}>
          {/* Success Header */}
          <Card className="shadow mb-4 border-success">
            <Card.Body className="p-4 text-center">
              <div className="display-1 mb-3">🎯</div>
              <h2 className="mb-3">You're All Set{businessName ? `, ${businessName}` : ''}!</h2>
              <p className="lead text-muted mb-0">
                Your Exit Strategy and Battle Plan are ready. You have everything you need to start executing.
              </p>
            </Card.Body>
          </Card>

          {/* What You've Completed */}
          <Card className="shadow mb-4">
            <Card.Body className="p-4">
              <h4 className="mb-3">What You've Completed</h4>
              <Row>
                <Col md={4} className="mb-3 mb-md-0">
                  <div className="d-flex align-items-center">
                    <Badge bg="success" className="me-2">✓</Badge>
                    <div>
                      <strong>Business Profile</strong>
                      <div className="small text-muted">Products, pricing, margins</div>
                    </div>
                  </div>
                </Col>
                <Col md={4} className="mb-3 mb-md-0">
                  <div className="d-flex align-items-center">
                    <Badge bg="success" className="me-2">✓</Badge>
                    <div>
                      <strong>Exit Strategy</strong>
                      <div className="small text-muted">{exitStrategy || 'Configured'}</div>
                    </div>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="d-flex align-items-center">
                    <Badge bg="success" className="me-2">✓</Badge>
                    <div>
                      <strong>Battle Plan</strong>
                      <div className="small text-muted">{targetAmount ? `Target: ${targetAmount}` : 'Month-by-month roadmap'}</div>
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Primary Action */}
          <Card className="shadow mb-4 bg-primary text-white">
            <Card.Body className="p-4 text-center">
              <h4 className="mb-3">Your Battle Plan is Ready</h4>
              <p className="mb-4">
                See your month-by-month execution roadmap with revenue projections,
                marketing capacity recommendations, and strategic milestones.
              </p>
              <Button
                variant="light"
                size="lg"
                onClick={() => navigate('/battle-plan')}
              >
                View Your Battle Plan →
              </Button>
            </Card.Body>
          </Card>

          {/* Other Quick Actions */}
          <Row className="mb-4">
            <Col md={6} className="mb-3 mb-md-0">
              <Card className="shadow h-100">
                <Card.Body className="p-3 text-center">
                  <h6 className="mb-2">Exit Strategy Report</h6>
                  <p className="small text-muted mb-2">Full analysis and recommendations</p>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => navigate('/updated-exit-strategy')}
                  >
                    View Report
                  </Button>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className={`shadow h-100 ${isExploringGuided ? 'border-warning' : ''}`}>
                <Card.Body className="p-3 text-center">
                  <h6 className="mb-2">Product Catalog</h6>
                  <p className="small text-muted mb-2">
                    {isExploringGuided
                      ? 'Edit products, pricing, and payment type (one-time vs recurring)'
                      : 'Edit your products & pricing'}
                  </p>
                  <Button
                    variant={isExploringGuided ? 'warning' : 'outline-primary'}
                    size="sm"
                    onClick={() => navigate('/product-catalog')}
                  >
                    {isExploringGuided ? 'Edit Products' : 'Manage Products'}
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Guided Journey Option */}
          <Card className={`shadow ${isExploringGuided ? 'border-success' : 'border-info'}`}>
            <Card.Body className="p-4">
              <Row className="align-items-center">
                <Col md={8}>
                  {isExploringGuided ? (
                    <>
                      <h5 className="mb-2 text-success">Currently Exploring Guided Journey</h5>
                      <p className="text-muted mb-0">
                        You're in <strong>exploration mode</strong>. You can edit any data you entered
                        during quick-start and access detailed modules for brand identity, marketing strategy,
                        competitive analysis, and asset generation.
                      </p>
                    </>
                  ) : (
                    <>
                      <h5 className="mb-2">Want to Go Deeper?</h5>
                      <p className="text-muted mb-0">
                        The <strong>Guided Journey</strong> offers detailed modules for brand identity,
                        marketing strategy, competitive analysis, and asset generation. It takes more time
                        but provides comprehensive business positioning and ready-to-use marketing materials.
                      </p>
                    </>
                  )}
                </Col>
                <Col md={4} className="text-md-end mt-3 mt-md-0">
                  {isExploringGuided ? (
                    <Button
                      variant="success"
                      onClick={() => navigate('/dashboard')}
                    >
                      Go to Full Dashboard →
                    </Button>
                  ) : (
                    <Button
                      variant="info"
                      onClick={handleExploreGuidedJourney}
                    >
                      Explore Guided Journey
                    </Button>
                  )}
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Info Alert */}
          <Alert variant="light" className="mt-4 border">
            <strong>Note:</strong> You can always return here by clicking "Quick Start Dashboard" in the navigation,
            or switch to the full Guided Journey anytime to complete additional modules.
          </Alert>
        </Col>
      </Row>
    </Container>
  );
}

export default QuickStartDashboard;
